import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Table, Tag, Space, message } from "antd";
import ApiTaskGraphMigration from "../../../api/task-graph-migration";

// LF360-1815: tech page for the regular-task → editor-graph migration.
// Shows the persisted per-task journal, live run status, and lets a
// systemAdmin start a (re)run — errored tasks are retried, migrated skipped.
const STATUS_COLORS = { ok: "green", error: "red", skipped: "default" };

const TaskGraphMigration = () => {
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState([]);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [statusResp, journalResp] = await Promise.all([
        ApiTaskGraphMigration.getStatus(),
        ApiTaskGraphMigration.getJournal(),
      ]);
      setStatus(statusResp?.data?.status || null);
      setRows(journalResp?.data?.rows || []);
      setTotals(journalResp?.data?.totals || []);
    } catch (e) {
      message.error("Failed to load migration state");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [refresh]);

  useEffect(() => {
    if (status?.status === "running" && !pollRef.current) {
      pollRef.current = setInterval(refresh, 3000);
    }
    if (status?.status !== "running" && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [status, refresh]);

  const handleStart = async () => {
    const resp = await ApiTaskGraphMigration.start();
    if (resp?.data?.started === false) {
      message.info("Migration is already running");
    }
    refresh();
  };

  const handleStop = async () => {
    await ApiTaskGraphMigration.stop();
    refresh();
  };

  const columns = [
    {
      title: "Task",
      key: "task",
      render: (_, row) => row.DailyTask?.title || row.dailyTaskId,
    },
    {
      title: "Row status",
      dataIndex: "taskRowStatus",
      key: "taskRowStatus",
      width: 110,
    },
    {
      title: "Result",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (value) => <Tag color={STATUS_COLORS[value] || "default"}>{value}</Tag>,
      filters: [
        { text: "ok", value: "ok" },
        { text: "error", value: "error" },
        { text: "skipped", value: "skipped" },
      ],
      onFilter: (value, row) => row.status === value,
    },
    {
      title: "Questions",
      dataIndex: "questionCount",
      key: "questionCount",
      width: 100,
    },
    {
      title: "Error",
      dataIndex: "error",
      key: "error",
      render: (value) => value || "—",
    },
  ];

  const runInfo = status
    ? `${status.status} — processed ${status.processed}/${status.total}, migrated ${status.migrated}, skipped ${status.skipped}, failed ${status.failed}`
    : "";

  return (
    <div style={{ padding: 24 }}>
      <h2>Task graph migration (LF360-1815)</h2>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleStart} disabled={status?.status === "running"}>
          {totals.some((t) => t.status === "error") ? "Rerun (retry failed)" : "Start"}
        </Button>
        <Button onClick={handleStop} disabled={status?.status !== "running"}>
          Stop
        </Button>
        <Button onClick={refresh}>Refresh</Button>
        <span>{runInfo}</span>
      </Space>
      <div style={{ marginBottom: 12 }}>
        {totals.map((t) => (
          <Tag key={t.status} color={STATUS_COLORS[t.status] || "default"}>
            {t.status}: {t.count}
          </Tag>
        ))}
      </div>
      <Table
        rowKey="id"
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{ pageSize: 25 }}
      />
    </div>
  );
};

export default TaskGraphMigration;
